import { type SubmitHandler, useForm } from 'react-hook-form';

import {
    Alert,
    Code,
    Description,
    Dialog,
    DialogBody,
    DialogCloseButton,
    DialogFooter,
    DialogHeader,
    FieldError,
    FieldGroup,
    Fieldset,
    Form,
    Heading,
    Label,
    Submit,
    TextField,
    TextInput,
    useDialogAction,
} from '@gitbook/spine-react';
import { isHttpUrl } from '@gitbook/util-url';

import { useSetAnalyticsScreen } from '../../navigation';
import type { ImportSourceDialogProps } from './types';
import { useRunImport } from './util';

export function ImportWebsiteDialog(props: ImportSourceDialogProps): React.ReactElement {
    return (
        <Dialog>
            <DialogHeader>
                <Heading>Import Online Docs</Heading>
                <Description>
                    GitBook will import all public pages from this URL (up to 200 pages).
                </Description>
            </DialogHeader>
            <ImportSiteForm {...props} />
        </Dialog>
    );
}

type WebsiteImportFormValues = {
    url: string;
};

function ImportSiteForm(props: ImportSourceDialogProps) {
    const { resolveTarget, onStart, trigger } = props;

    useSetAnalyticsScreen({ screen: 'import_start_source', source: 'website', trigger }, [trigger]);

    const form = useForm<WebsiteImportFormValues>({
        defaultValues: {
            url: '',
        },
    });

    const [isPending, startDialogAction] = useDialogAction();

    const runImport = useRunImport();

    const onSubmit: SubmitHandler<WebsiteImportFormValues> = async (data) => {
        await startDialogAction(async ({ close }) => {
            await runImport({
                trigger,
                resolveTarget,
                onStart: async (target) => {
                    await onStart?.(target);
                    await close();
                },
                source: {
                    type: 'website' as const,
                    url: data.url,
                },
            });
        });
    };
    return (
        <Form form={form} onSubmit={form.handleSubmit(onSubmit)}>
            <DialogBody>
                <Fieldset>
                    <FieldGroup>
                        <TextField
                            required
                            name="url"
                            validate={(value) => {
                                if (value && !isHttpUrl(value)) {
                                    return 'Invalid URL';
                                }
                                return undefined;
                            }}
                        >
                            <Label>URL</Label>
                            <Description>Enter the docs link you want to import.</Description>
                            <TextInput
                                autoFocus
                                type="url"
                                disabled={isPending}
                                placeholder="https://website.com/docs/"
                            />
                            <FieldError />
                        </TextField>
                    </FieldGroup>
                </Fieldset>
                <Alert
                    className="mt-4"
                    kind="info"
                    details={
                        <>
                            <p>
                                GitBook only imports pages under the URL you enter.
                                <br />
                                For example, if you import <Code>website.com/docs/</Code>, it will
                                include <Code>website.com/docs/article</Code>, but not{' '}
                                <Code>website.com/other-parent/page</Code>.
                            </p>
                            <p className="mt-2">
                                Imports are limited to 200 pages. For larger sites, import smaller
                                sub-paths separately.
                            </p>
                        </>
                    }
                />
            </DialogBody>
            <DialogFooter>
                <DialogCloseButton />
                <Submit label="Start import" />
            </DialogFooter>
        </Form>
    );
}
